import { FavoriteDetailResponse } from '@/api/services/shintyokuService';
import { DialogSearchSelectedData, JobSearchCardData } from '@/api/services/trnSerchMyPageService';
import { MYPAGE_CONSTANTS } from '@/constants/mypage';
import { Job, JobSearchSelectedCheckboxListItem } from '@/types';
import { DialogOccupData, DialogPrefData, JobSearchPublicDropdownData } from '@/types/mstMyPage';
import _, { isArray, isString } from 'lodash';
import { Store } from 'vuex';
import { getMessageForInvalidOrders } from './messageFormat';
import { getCareerInfoConditionSearchService } from '@/api/services/trnCareerService';

export const DEFAULT_KEYWORD_FIELD = 'company_name';
const storeBase = MYPAGE_CONSTANTS.STORE_MST;

export const jobSearchUtils = {
  defaultSearch: <DialogSearchSelectedData>{
    keyword: {
      field: DEFAULT_KEYWORD_FIELD,
      value: '',
    },
    jobsearchAnualslry: {
      id: 0,
      text: MYPAGE_CONSTANTS.STRING.JOB_SEARCH_NOT_SPECIFIED_VALUE
    },
    location: {
      jobsearchAreaid: [],
      jobsearchPrefid: [],
      jobsearchCityid: [],
      displayLocationList: [],
    },
    occupation: {
      jobsearchOcupctg1id: [],
      jobsearchOcupctg2id: [],
      jobsearchOcupctg3id: [],
      displayOccupList: []
    },
    industry: {
      jobsearchIndsctgGroupId: [],
      jobsearchIndsctgId: [],
      displayIndustryList: [],
    },
    jobsearchPossibleOvertime: {
      id: 0,
      text: MYPAGE_CONSTANTS.STRING.JOB_SEARCH_NOT_SPECIFIED_VALUE
    },
    jobsearchRemotework: {
      id: 0,
      text: MYPAGE_CONSTANTS.STRING.JOB_SEARCH_NOT_SPECIFIED_VALUE
    },
    jobsearchSideBusiness: undefined,
    jobsearchFeatures: [],
    jobsearchFeaturesOrder: [],

    isSaveSearchCondition: false,
  },
  defaultSearchCard: <JobSearchCardData>{
    srchId: 0,
    annualIncome: MYPAGE_CONSTANTS.STRING.JOB_SEARCH_NOT_SPECIFIED_VALUE,
    location: MYPAGE_CONSTANTS.STRING.JOB_SEARCH_NOT_SPECIFIED_VALUE,
    occupation: MYPAGE_CONSTANTS.STRING.JOB_SEARCH_NOT_SPECIFIED_VALUE,
    other: '',
    isSaved: false,
  },

  loadSearchConditionFromSessionStorage(): DialogSearchSelectedData {
    try {
      const item = sessionStorage.getItem(MYPAGE_CONSTANTS.SESSION_KEY.JOBSEARCH_PUBLIC_SEARCHCOND);
      if (!item) return _.cloneDeep(this.defaultSearch);

      const currentSearchCond = JSON.parse(item);
      if (currentSearchCond && typeof currentSearchCond === 'object') {
        const result = { ...currentSearchCond, isSaveSearchCondition: false };
        return result as DialogSearchSelectedData;
      }
    } catch (err) {
      console.error(err);
      sessionStorage.removeItem(MYPAGE_CONSTANTS.SESSION_KEY.JOBSEARCH_PUBLIC_SEARCHCOND);
    }
    return _.cloneDeep(this.defaultSearch);
  },

  loadSearchCardFromSessionStorage(): JobSearchCardData | null {
    try {
      const item = sessionStorage.getItem(MYPAGE_CONSTANTS.SESSION_KEY.JOBSEARCH_PUBLIC_SEARCHCARD);
      if (!item) return null;

      const currentSearchCard = JSON.parse(item);
      if (currentSearchCard && typeof currentSearchCard === 'object') {
        return currentSearchCard as JobSearchCardData;
      }
    } catch (err) {
      console.error(err);
      sessionStorage.removeItem(MYPAGE_CONSTANTS.SESSION_KEY.JOBSEARCH_PUBLIC_SEARCHCARD);
    }
    return null;
  },

  saveSearchConditionToSessionStorage(searchData: DialogSearchSelectedData) {
    sessionStorage.setItem(MYPAGE_CONSTANTS.SESSION_KEY.JOBSEARCH_PUBLIC_SEARCHCOND, JSON.stringify(searchData));
  },
  saveSearchCardToSessionStorage(searchCard?: JobSearchCardData) {
    if (!searchCard) {
      searchCard = this.defaultSearchCard;
    }
    sessionStorage.setItem(MYPAGE_CONSTANTS.SESSION_KEY.JOBSEARCH_PUBLIC_SEARCHCARD, JSON.stringify(searchCard));
  },

  async setCurrentSearchInfo(store: Store<any>, searchData: DialogSearchSelectedData) {
    // master data
    const mstAnualSlry = await store.dispatch(storeBase.MST_ANUAL_SLRY) as JobSearchPublicDropdownData[];
    const mstOvertime = await store.dispatch(storeBase.MST_OVERTIME) as JobSearchPublicDropdownData[];
    const mstRemoteWorkOrder = await store.dispatch(storeBase.MST_REMOTE_WORK_ORDER) as JobSearchPublicDropdownData[];
    const mstSideBusinessOrder = await store.dispatch(storeBase.MST_SIDE_BUSINESS_ORDER) as JobSearchPublicDropdownData[];
    const mstFeatures = await store.dispatch(storeBase.MST_FEATURES) as JobSearchPublicDropdownData[];
    const mstFeaturesOrder = await store.dispatch(storeBase.MST_FEATURES_ORDER) as JobSearchPublicDropdownData[];
    const getMstName = (mstData: JobSearchPublicDropdownData[], id: string | Array<number|string>) => {
      if (isString(id)){
        return mstData.find(mst => mst.id == id)?.text;
      } else if (isArray(id)) {
        const strIds = id.map(i => i.toString());
        return mstData.filter(mst => strIds.includes(mst.id.toString())).map(mst => mst.text).join('、');
      }
      return '';
    };
    // 年収
    const income = searchData.jobsearchAnualslry;
    let incomLable = MYPAGE_CONSTANTS.STRING.JOB_SEARCH_NOT_SPECIFIED_VALUE;
    if (income && income.id){
      incomLable = getMstName(mstAnualSlry, income.id.toString()) || MYPAGE_CONSTANTS.STRING.JOB_SEARCH_NOT_SPECIFIED_VALUE;
    }

    // 勤務地
    let locations = searchData
                  .location
                  .displayLocationList
                  .filter(loc => loc.isChecked)
                  .map(loc => loc.text);
    if (locations.length == 0){
      locations = [MYPAGE_CONSTANTS.STRING.JOB_SEARCH_NOT_SPECIFIED_VALUE];
    } else if (locations.length > 1) {
      locations = [locations[0], 'ほか'];
    }

    // 職種
    let occups = searchData
                  .occupation
                  .displayOccupList
                  .filter(oc => oc.isChecked)
                  .map(oc => oc.text);
    if (occups.length == 0){
      occups = [MYPAGE_CONSTANTS.STRING.JOB_SEARCH_NOT_SPECIFIED_VALUE];
    } else if (occups.length > 1) {
      occups = [occups[0], 'ほか'];
    }

    // その他
    const others: string[] = [];
    // フリーキーワード
    const keyword = searchData.keyword.value;
    if (keyword){
      others.push(keyword);
    }
    // 業種
    const industries = searchData
                    .industry
                    .displayIndustryList
                    .filter(ind => ind.isChecked)
                    .map(ind => ind.text);
    if (industries.length) {
      others.push('業種 ' + industries.join('、'));
    }
    // 残業時間（月間）
    const overtime = searchData.jobsearchPossibleOvertime;
    if (overtime?.id){
      others.push('残業時間（月間） ' + getMstName(mstOvertime, overtime.id.toString()));
    }
    // リモートワークの有無
    const rmWork = searchData.jobsearchRemotework;
    if (rmWork?.id){
      others.push('リモートワークの有無 ' + getMstName(mstRemoteWorkOrder, rmWork.id.toString()));
    }
    // 副業の可否
    const sideJob = searchData.jobsearchSideBusiness;
    if (sideJob){
      others.push('副業の可否 ' + getMstName(mstSideBusinessOrder, sideJob.toString()));
    }
    // 企業の特性
    const workCnd = searchData.jobsearchFeatures;
    if (workCnd && workCnd.length) {
      others.push('企業の特性 ' + getMstName(mstFeatures, workCnd));
    }
    // こだわり
    const jobDesc = searchData.jobsearchFeaturesOrder;
    if (jobDesc && jobDesc.length) {
      others.push('こだわり ' + getMstName(mstFeaturesOrder, jobDesc));
    }
    
    // maxlength = 60
    let strOther = others.join('｜');
    if (strOther.length > 60) {
      strOther = strOther.substring(0, 60) + '...';
    }
    const currentSearch: JobSearchCardData = {
      annualIncome: incomLable,
      location: locations.join('、'),
      occupation: occups.join('、'),
      other: strOther,
      isSaved: false,
    };
    sessionStorage.setItem(MYPAGE_CONSTANTS.SESSION_KEY.JOBSEARCH_PUBLIC_SEARCHCARD, JSON.stringify(currentSearch));
    return currentSearch;
  },

  updateLocationSearchData(searchData: DialogSearchSelectedData, chk: JobSearchSelectedCheckboxListItem, isChecked: boolean) {
    const locations = searchData.location;
    chk.isChecked = isChecked;
    const areaIdx = locations.jobsearchAreaid.findIndex(loc => loc == chk.id);

    if (chk.level == 1) {
      if (areaIdx >= 0 && !isChecked) {
        locations.jobsearchAreaid = [
          ...locations.jobsearchAreaid.slice(0, areaIdx),
          ...locations.jobsearchAreaid.slice(areaIdx + 1)
        ];
      } else {
        locations.jobsearchAreaid = [chk.id, ...locations.jobsearchAreaid];
      }
      return;
    }

    const prefIdx = locations.jobsearchPrefid.findIndex(loc => loc == Number(chk.id));
    if (chk.level == 2) {
      if (prefIdx >= 0 && !chk.isChecked) {
        locations.jobsearchPrefid = [
          ...locations.jobsearchPrefid.slice(0, prefIdx),
          ...locations.jobsearchPrefid.slice(prefIdx + 1)
        ];
      } else {
        locations.jobsearchPrefid = [Number(chk.id), ...locations.jobsearchPrefid];
      }
      return;
    }

    const cityIdx = locations.jobsearchCityid.findIndex(ind => ind == Number(chk.id));
    if (cityIdx >= 0 && !chk.isChecked) {
      locations.jobsearchCityid = [
        ...locations.jobsearchCityid.slice(0, cityIdx),
        ...locations.jobsearchCityid.slice(cityIdx + 1)
      ];
    } else {
      locations.jobsearchCityid = [Number(chk.id), ...locations.jobsearchCityid];
    }
  },

  updateOccupationSearchData(searchData: DialogSearchSelectedData, chk: JobSearchSelectedCheckboxListItem, isChecked: boolean){
    const occups = searchData.occupation;
    chk.isChecked = isChecked;
    const occup1Idx = occups.jobsearchOcupctg1id.findIndex(ind => ind == chk.id);

    if (chk.level == 1){
      if (occup1Idx >= 0 && !isChecked) {
        occups.jobsearchOcupctg1id = [
          ...occups.jobsearchOcupctg1id.slice(0, occup1Idx),
          ...occups.jobsearchOcupctg1id.slice(occup1Idx + 1)
        ];
      } else {
        occups.jobsearchOcupctg1id = [chk.id, ...occups.jobsearchOcupctg1id];
      }
      return;
    }

    const occup2Idx = occups.jobsearchOcupctg2id.findIndex(ind => ind == chk.id);
    if (chk.level == 2){
      if (occup2Idx >= 0 && !chk.isChecked) {
        occups.jobsearchOcupctg2id = [
          ...occups.jobsearchOcupctg2id.slice(0, occup2Idx),
          ...occups.jobsearchOcupctg2id.slice(occup2Idx + 1)
        ];
      } else {
        occups.jobsearchOcupctg2id = [chk.id, ...occups.jobsearchOcupctg2id];
      }
      return;
    }

    const occup3Idx = occups.jobsearchOcupctg3id.findIndex(ind => ind == chk.id);
    if (occup3Idx >= 0 && !chk.isChecked) {
      occups.jobsearchOcupctg3id = [
        ...occups.jobsearchOcupctg3id.slice(0, occup3Idx),
        ...occups.jobsearchOcupctg3id.slice(occup3Idx + 1)
      ];
    } else {
      occups.jobsearchOcupctg3id = [chk.id, ...occups.jobsearchOcupctg3id];
    }
  },

  updateIndustrySearchData(searchData: DialogSearchSelectedData, chk: JobSearchSelectedCheckboxListItem, isChecked: boolean){
    const inds = searchData.industry;
    chk.isChecked = isChecked;
    const groupIdx = inds.jobsearchIndsctgGroupId.findIndex(ind => ind == chk.id);

    if (chk.level == 1){
      if (groupIdx >= 0 && !isChecked) {
        inds.jobsearchIndsctgGroupId = [
          ...inds.jobsearchIndsctgGroupId.slice(0, groupIdx),
          ...inds.jobsearchIndsctgGroupId.slice(groupIdx + 1)
        ];
      } else {
        inds.jobsearchIndsctgGroupId = [chk.id, ...inds.jobsearchIndsctgGroupId];
      }
      return;
    }
    const idx = inds.jobsearchIndsctgId.findIndex(ind => ind == Number(chk.id));
    if (idx >= 0 && !chk.isChecked) {
      inds.jobsearchIndsctgId = [
        ...inds.jobsearchIndsctgId.slice(0, idx),
        ...inds.jobsearchIndsctgId.slice(idx + 1)
      ];
    } else {
      inds.jobsearchIndsctgId = [Number(chk.id), ...inds.jobsearchIndsctgId];
    }
  },

  showMessageForInvalidOrders(store: Store<any>, jobList: Job[],detailResp: FavoriteDetailResponse, isBulkAction = false){
    let errMesg = '';
    const ignoreOrder = detailResp.ignoreOrder;
    if (ignoreOrder.length) {
      const ignoreJobs = jobList.filter(j => ignoreOrder.includes(Number(j.orderId)))
                        .map(j => ({
                          orderId: Number(j.orderId),
                          realClientName: j.companyName,
                          positionName: j.jobTitle
                        }));
      errMesg += (isBulkAction ? MYPAGE_CONSTANTS.STRING.FAVORITE_ERROR_MSG_BULK : MYPAGE_CONSTANTS.STRING.IGNORED_ORDER_ERR_MESG_SINGLE)
                + getMessageForInvalidOrders(ignoreJobs);
    }
    const unApprovedOrder = detailResp.unApprovedOrder;
    if (unApprovedOrder.length) {
      const unApprovedJobs = jobList.filter(j => unApprovedOrder.includes(Number(j.orderId)))
                        .map(j => ({
                          orderId: Number(j.orderId),
                          realClientName: j.companyName,
                          positionName: j.jobTitle
                        }));
      errMesg += MYPAGE_CONSTANTS.STRING.NO_ACCESS_RIGHTS
                + getMessageForInvalidOrders(unApprovedJobs);
    }
    if (errMesg){
      store.dispatch('toasts/appendTimeToast', {label: errMesg});
    }
  },

  toggleDialogSearch(eleId: string, isDisabled = true) {
    const ele = document.getElementById(eleId) as HTMLInputElement;
    if (ele) {
      const editableEles = ele.querySelectorAll('input, select, textarea, button');
      editableEles.forEach(e => (e as HTMLInputElement).disabled = isDisabled);
    }
  },

  async loadDefaultSearchCondition(store: Store<any>, dialogSearchSelectedData: DialogSearchSelectedData) {
    const defaultSearchCond = await getCareerInfoConditionSearchService();

    // 年収
    const incomes = await store.dispatch(storeBase.MST_ANUAL_SLRY) as JobSearchPublicDropdownData[];
    const careerIncome = defaultSearchCond.currentAnnualIncomeNum;
    if (dialogSearchSelectedData.jobsearchAnualslry && careerIncome) {
      for (let i = incomes.length - 1; i >= 0; i--) {
        if (Number(incomes[i].value) <= careerIncome) {
          dialogSearchSelectedData.jobsearchAnualslry.id = Number(incomes[i].id);
          break;
        }
      }
    }

    // 勤務地
    const {location} = dialogSearchSelectedData;
    if (defaultSearchCond.prefId == MYPAGE_CONSTANTS.MSTPREF_NATIONAL_ID) {
      location.jobsearchPrefid = [];
    } else if (defaultSearchCond.prefId == MYPAGE_CONSTANTS.MSTPREF_FOREIGN_ID) {
      location.jobsearchAreaid = [MYPAGE_CONSTANTS.MSTAREA_FOREIGN_ID];
      location.jobsearchPrefid = [];
    } else {
      location.jobsearchPrefid = [defaultSearchCond.prefId];
    }

    if (location.jobsearchPrefid.length > 0 || location.jobsearchAreaid.length > 0) {
      const locations = await store.dispatch(storeBase.DIALOG_PREF) as DialogPrefData;
      let isFound = false;
      
      locations.checkbox1.forEach(box1 => {
        if (isFound) return;
        if (box1.id == MYPAGE_CONSTANTS.MSTAREA_FOREIGN_ID && defaultSearchCond.prefId == MYPAGE_CONSTANTS.MSTPREF_FOREIGN_ID) {
          location.displayLocationList.push({
            id: box1.value,
            text: `${box1.text}`,
            isChecked: true,
            level: 1,
          });
          isFound = true;
          return;
        }

        box1.checkbox2.forEach(box2 => {
          const prefId = parseInt(box2.id.toString());
          if (prefId == MYPAGE_CONSTANTS.MSTPREF_FOREIGN_ID) return;
          if (!isFound && !isNaN(prefId) && prefId == defaultSearchCond.prefId) {
            location.displayLocationList.push({
              id: box2.value,
              text: `${box1.text} ${box2.text}`,
              isChecked: true,
              level: 2,
            });
            isFound = true;
          }
        });
      });
    }

    // 職種
    dialogSearchSelectedData.occupation.jobsearchOcupctg3id = [
      defaultSearchCond.expOcupcTg1Id,
      defaultSearchCond.expOcupcTg2Id,
      defaultSearchCond.expOcupcTg3Id,
      defaultSearchCond.expOcupcTg4Id,
      defaultSearchCond.expOcupcTg5Id,
      defaultSearchCond.expOcupcTg6Id,
    ].filter(id => id);

    const occups = await store.dispatch(storeBase.DIALOG_OCCUPATION) as DialogOccupData;
    occups.selectbox1.forEach(box1 => {
      box1.selectbox2.forEach(box2 => {
          box2.selectbox3.forEach(box3 => {
            if (dialogSearchSelectedData.occupation.jobsearchOcupctg3id.includes(box3.value)) {
              dialogSearchSelectedData.occupation.displayOccupList.push({
                id: box3.value,
                text: `${box1.text} ${box2.text} - ${box3.text}`,
                isChecked: true,
                level: 3,
              });
            }
          });
      });
    });

    sessionStorage.setItem(MYPAGE_CONSTANTS.SESSION_KEY.JOBSEARCH_PUBLIC_SEARCHCOND,
          JSON.stringify(dialogSearchSelectedData));
  }
};

export function formatNumber(value: number) {
  if(value == null){
    return '0';
  }
  if (value > 9999) {
    return '9,999+';
  }
  return value.toLocaleString('en-US');
}